import {Model} from 'backbone';

import {Permissions} from 'auth/PermissionConstants';
import * as statusbar from 'components/StatusBar';
import {SEQUENCE_TYPE} from 'constants/FileBrowserConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {NOTE_TIMESTAMPS} from 'settings/visibility-options/VisibilityOptions';

export const DEFAULT_PREVIEW_OPTIONS = {
  showOpenLink: true,
  followLink: false,
};

export interface Preview {
  followLink: boolean;
  showOpenLink: boolean;
}

// Props that are used by all XXNoteView components
export interface SharedNoteViewProps {
  allowEdits: boolean;
  cidIdMap?: {[cid: string]: number};
  displayOptions?: object | null;
  fileId: string;
  fileType: string;
  historyResetCount?: number;
  humanId?: string | null;
  permissions: Permissions;
  preview?: Preview;
  printLayout: boolean;
  showPrintMargins: boolean;
  showTableOfContents: boolean;
  showTimestamps: boolean;
  spellCheck: boolean;
}

export function getSharedNoteViewProps(backboneModel: Model): SharedNoteViewProps {
  let fileType = backboneModel.get('model').fileType();
  if (
    fileType === ModelTypeConstants.SEQUENCE &&
    backboneModel.get('model')._sequence().get('type') === SEQUENCE_TYPE.OLIGO
  ) {
    fileType = NoteConstants.OLIGO;
  }
  const displayOptions = backboneModel.get('displayOptions');
  const baseProps = {
    fileType,
    displayOptions,
    fileId: backboneModel.get('model').id,
    cidIdMap: backboneModel.get('model').get('cidIdMap'),
    humanId: backboneModel.get('model').humanId(),
    allowEdits: backboneModel.get('model').allowEditsToNote(),
    permissions: backboneModel.get('model').permissions(),
    historyResetCount: backboneModel.get('model').get('historyResetCount'),
    preview: DEFAULT_PREVIEW_OPTIONS,
    fullScreen: true,
  };
  if (displayOptions == null) {
    return {
      ...baseProps,
      showTimestamps: false,
      spellCheck: false,
      showTableOfContents: false,
      printLayout: false,
      showPrintMargins: false,
    };
  }
  const printLayoutOption = displayOptions.getOption(vo.ELN_PRINT_LAYOUT.name);
  return {
    ...baseProps,
    showTimestamps: displayOptions.getOption(NOTE_TIMESTAMPS.name).get('visible'),
    spellCheck: displayOptions.getOption(vo.ELN_SPELL_CHECK.name).get('visible'),
    showTableOfContents: displayOptions.getOption(vo.ELN_TABLE_OF_CONTENTS.name).get('visible'),
    printLayout: printLayoutOption.get('visible'),
    showPrintMargins: printLayoutOption.isSettingChecked(vo.ELN_SHOW_PRINT_MARGINS.name),
  };
}

export type GeneralNoteViewProps = SharedNoteViewProps & DedicatedGeneralNoteViewProps;

interface DedicatedGeneralNoteViewProps {
  parentType: string;
  banner?: string;
  updateStatusBar?: (msg: string) => void;
  clearStatusBar?: () => void;
  // Controls whether the editor is rendered with position: absolute.
  fullScreen: boolean;
  // Controls what's included in the editor toolbar
  minimalToolbar?: boolean;
}

export function getDedicatedGeneralNoteViewProps(
  backboneModel: Model,
  overrides: Partial<DedicatedGeneralNoteViewProps> = {}
): DedicatedGeneralNoteViewProps {
  const banner = backboneModel.get('model').getBannerText();
  return {
    banner: backboneModel.get('model').allowEditsToNote() ? undefined : banner,
    parentType: 'Note',
    updateStatusBar: (message: string) => statusbar.showOverlayMessage(message),
    clearStatusBar: () => statusbar.hideOverlayMessage(),
    fullScreen: true,
    minimalToolbar: false,
    ...overrides,
  };
}
